import {useCallback} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {navigateToRoute, useCurrentRoute} from '~/CreateFabricGreetingCard/navigation';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricGreetingCard/reducers/fabricDesign/constants';
import {selectSavingStep} from '~/CreateFabricGreetingCard/reducers/fabricDesign/selectors';
import {
  selectIsPremium,
  selectIsProOrPartyPass,
} from '~/CreateFabricGreetingCard/reducers/fabricTemplate/selectors';
import {useUyo} from '~/common/fabric/Card/hooks/useUyo';
import {CardFeaturesPanel} from '~/common/fabric/CreateDesign/components/NavBottom/CardFeaturesPanel';
import {isInCreateFlowNavABTest} from '~/common/fabric/experiments';
import {selectFabricTemplateUsedAs} from '~/common/fabric/reducers/fabricTemplate/selector';
import {isGreetingCardByUsedAs} from '~/common/utils/event_utils';
import {Button} from '~library/atoms/Button';
import {toastAlert} from '~library/atoms/Toast';
import {LoadingOverlay} from '~library/molecules/LoadingOverlay';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';

import './NavBottom.sass';

export const NavBottom = () => {
  const store = useStore();
  const isPremium = useSelector(selectIsPremium);
  const isProOrPartyPass = useSelector(selectIsProOrPartyPass);
  const savingStep = useSelector(selectSavingStep);
  const currentRoute = useCurrentRoute();
  const dispatch = useDispatch();
  const {isUyo, pristineUyo} = useUyo();

  const stopSubmitting = useCallback(() => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {savingStep: false},
    });
  }, [dispatch]);

  const onClickHandler = useCallback(async () => {
    let errorState = false;

    if (isUyo && pristineUyo) {
      stopSubmitting();
      const toastId = 'design__toast__photo';

      // This will prevent showing multiple toasts for this action
      if (!document.getElementById(toastId)) {
        toastAlert('Please upload photo on front card before continuing', {
          type: 'error',
          icon: <AlertLineIcon ratio={0.65} />,
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          id: toastId,
        });
      }
      errorState = true;
    }

    if (!errorState) {
      const navigating = await navigateToRoute(currentRoute.nextRoute, store);
      if (!navigating) stopSubmitting();
    }
  }, [isUyo, pristineUyo, stopSubmitting, currentRoute.nextRoute, store]);

  const onClick = useCallback(async () => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {savingStep: true},
    });
    await onClickHandler();
  }, [dispatch, onClickHandler]);

  const isInNavABTest = isInCreateFlowNavABTest();
  const usedAs = useSelector(selectFabricTemplateUsedAs);
  const isCard = isGreetingCardByUsedAs(usedAs);

  if (isInNavABTest && !currentRoute.meta.editing && !isCard) return null;

  return (
    <div className="create-design__nav-bottom">
      <div className="create-design__nav-bottom__buttons_wrapper">
        <div className="create-design__nav-bottom__panel">
          {(isPremium || isProOrPartyPass) && <CardFeaturesPanel />}
        </div>
      </div>

      <div className="create-design__nav-bottom__cta_wrapper">
        {currentRoute.customButton ? (
          currentRoute.customButton(onClick, {className: 'create-design__nav-bottom__next'})
        ) : (
          <Button
            id="navBottom"
            className="create-design__nav-bottom__next"
            data-qa-id="nav-bottom-next"
            variant="primary"
            size="large"
            ariaLabel="Next"
            onClick={onClick}
          >
            {currentRoute.nextButtonText}
          </Button>
        )}
      </div>
      <LoadingOverlay loadingMessage="Saving..." show={savingStep} />
    </div>
  );
};
